import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const PdfJS = require("pdfjs-dist/legacy/build/pdf.min");
import CONFIG from "config/config";
import { insertCampaignLogs } from "Logs/campaign/gql";
import { CAMPAIGN_LOG_ENUM_TYPES } from "shared/constants";
import useDesignManagement from "pages/creative/pageV2/useDesignManagement";
import { authorization } from "module/auth";
import { getSignedUrl } from "graphQL/query/file";

// Set up the worker source
PdfJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PdfJS.version}/pdf.worker.min.js`;

interface UseDragAndDropProps {
  files: File[];
  setFiles: (files: File[]) => void;
  onFileUpload?: (file: any) => void;
  format: string;
}

interface FileRejection {
  errors: { code: string }[];
}

interface PDFPage {
  displayWidth: number;
  displayHeight: number;
  pageNumber: number;
  dataUrl: string;
}

export const useDragAndDropDesign = ({ setFiles, onFileUpload, format }: UseDragAndDropProps) => {
  const user = authorization.getUser();
  const { createDesign } = useDesignManagement({ clientId: user.clientId });

  const [tempFiles, setTempFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [pdfPages, setPdfPages] = useState<PDFPage[]>([]);
  const { campaignId } = useParams();

  // Debounce error state changes to prevent flashing
  const [debouncedError, setDebouncedError] = useState<string | null>(null);

  useEffect(() => {
    if (fileError) {
      const timer = setTimeout(() => {
        setDebouncedError(fileError);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setDebouncedError(null);
    }
  }, [fileError]);

  const loadAndRenderPDF = async (file: File): Promise<PDFPage[]> => {
    try {
      // Create object URL for the file
      const fileUrl = URL.createObjectURL(file);

      const loadingTask = PdfJS.getDocument(fileUrl);

      const pdf = await loadingTask.promise;
      const totalPages = Math.min(pdf.numPages, 2); // Only front and back
      const pagesArray: PDFPage[] = [];

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;

        // Calculate dimensions to fit in the preview area
        const maxWidth = 300;
        const maxHeight = 200;
        const aspectRatio = viewport.width / viewport.height;

        let displayWidth, displayHeight;
        if (aspectRatio > maxWidth / maxHeight) {
          displayWidth = maxWidth;
          displayHeight = maxWidth / aspectRatio;
        } else {
          displayHeight = maxHeight;
          displayWidth = maxHeight * aspectRatio;
        }

        pagesArray.push({
          dataUrl: canvas.toDataURL("image/png", 0.8),
          displayWidth,
          displayHeight,
          pageNumber: pageNum,
        });
      }

      URL.revokeObjectURL(fileUrl);
      return pagesArray;
    } catch (error) {
      console.error("Error loading PDF:", error);
      throw error;
    }
  };

  const createCombinedImage = async (pages: PDFPage[]): Promise<File> => {
    return new Promise((resolve, reject) => {
      console.log("Starting createCombinedImage with", pages.length, "pages");

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      // Calculate total width and height for combined image
      const margin = 20;
      const totalWidth = pages.reduce((sum, page) => sum + page.displayWidth, 0) + (pages.length - 1) * margin;
      const maxHeight = Math.max(...pages.map((page) => page.displayHeight));

      console.log("Canvas dimensions:", totalWidth, "x", maxHeight);

      canvas.width = totalWidth;
      canvas.height = maxHeight;

      // Set background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, totalWidth, maxHeight);

      // Load all images first, then draw them
      const images: HTMLImageElement[] = [];
      let loadedImages = 0;

      pages.forEach((page, index) => {
        console.log(`Loading image ${index + 1}/${pages.length}`);
        const img = new Image();
        img.onload = () => {
          console.log(`Image ${index + 1} loaded successfully`);
          images[index] = img;
          loadedImages++;

          // When all images are loaded, draw them to canvas
          if (loadedImages === pages.length) {
            console.log("All images loaded, drawing to canvas...");
            let currentX = 0;

            images.forEach((img, index) => {
              const page = pages[index];
              ctx.drawImage(img, currentX, (maxHeight - page.displayHeight) / 2, page.displayWidth, page.displayHeight);
              currentX += page.displayWidth + margin;
            });

            console.log("Canvas drawn, creating blob...");
            // Now create the blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  console.log("Blob created successfully, size:", blob.size);
                  const file = new File([blob], "thumbnail.png", { type: "image/png" });
                  resolve(file);
                } else {
                  console.error("Failed to create blob");
                  reject(new Error("Could not create blob"));
                }
              },
              "image/png",
              0.8
            );
          }
        };
        img.onerror = () => {
          console.error(`Failed to load image ${index}`);
          reject(new Error(`Failed to load image ${index}`));
        };
        img.src = page.dataUrl;
      });
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.some((f: File) => f.size > 50 * 1048576)) {
        setTempFiles([]);
        setFileError("Error: The file exceeded the 50mb limit");
      } else {
        setFileError(null);
        setTempFiles(acceptedFiles);
      }
    },
    [setTempFiles, setFileError]
  );

  const onDropRejected = (fileRejections: FileRejection[]) => {
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === "file-invalid-type") {
          setFileError("Error: You can upload only PDF file");
        }
      });
    });
  };

  const uploadHandler = async () => {
    const file = tempFiles[0];
    setFileError(null);
    setIsUploading(true);
    setFileUploadProgress(0);
    setFiles([file]);

    try {
      // Load and render PDF pages
      setFileUploadProgress(10);
      const pages = await loadAndRenderPDF(file);
      console.log("PDF pages loaded:", pages.length);
      setPdfPages(pages);

      setFileUploadProgress(30);

      // Create combined thumbnail image
      console.log("Creating combined image...");
      const thumbnailFile = await createCombinedImage(pages);
      console.log("Thumbnail file created:", thumbnailFile, "Size:", thumbnailFile.size);
      setFileUploadProgress(40);

      // Create S3 key for PDF
      const pdfKey = `campaign-builder/public/${Date.now().toString()}/${file.name}`;

      // Get signed URL for PDF upload
      const signedUrlPUT = await getSignedUrl(
        CONFIG.PDF_FLYER_BUCKET,
        pdfKey,
        "PUT",
        CONFIG.PDF_FLYER_BUCKET_REGION,
        "public-read"
      );

      // Upload PDF to S3 using signed URL
      const pdfResponse = await fetch(signedUrlPUT, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!pdfResponse.ok) {
        throw new Error("Failed to upload PDF file");
      }

      setFileUploadProgress(60);

      // Create S3 key for thumbnail
      const thumbnailKey = `campaign-builder/public/${Date.now().toString()}/thumbnail.png`;

      // Get signed URL for thumbnail upload
      const thumbnailSignedUrlPUT = await getSignedUrl(
        CONFIG.PDF_FLYER_BUCKET,
        thumbnailKey,
        "PUT",
        CONFIG.PDF_FLYER_BUCKET_REGION,
        "public-read"
      );

      // Upload thumbnail to S3
      const thumbnailResponse = await fetch(thumbnailSignedUrlPUT, {
        method: "PUT",
        body: thumbnailFile,
        headers: {
          "Content-Type": "image/png",
        },
      });

      if (!thumbnailResponse.ok) {
        throw new Error("Failed to upload thumbnail file");
      }

      setFileUploadProgress(80);

      // Get signed URLs for reading the uploaded files
      const signedUrlGET = await getSignedUrl(
        CONFIG.PDF_FLYER_BUCKET,
        pdfKey,
        "GET",
        CONFIG.PDF_FLYER_BUCKET_REGION,
        "public-read"
      );
      const thumbnailSignedUrlGET = await getSignedUrl(
        CONFIG.PDF_FLYER_BUCKET,
        thumbnailKey,
        "GET",
        CONFIG.PDF_FLYER_BUCKET_REGION,
        "public-read"
      );

      const design = await createDesign({
        filename: file.name,
        s3Link: signedUrlGET,
        thumbnailUrl: thumbnailSignedUrlGET,
        isUploaded: true,
        format,
        authorId: user.id,
      });

      await insertCampaignLogs([
        {
          campaignId: `${campaignId}`,
          type: CAMPAIGN_LOG_ENUM_TYPES.UPLOAD_PDF_DESIGN,
          additionalInfo: `[CB] CAMPAIGN EDIT File name: ${file.name}`,
        },
      ]);
      window.parent.postMessage(
        {
          type: "onDesignUploaded",
          data: {
            designId: design?.id,
            designName: file.name,
            s3Link: signedUrlGET,
          },
        },
        "*"
      );
      onFileUpload?.(design);
      setTempFiles([]);
      setFileUploadProgress(100);
    } catch (error) {
      setFileUploadProgress(0);
      console.error({ error }, "uploadFile error");
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (tempFiles.length > 0) {
      uploadHandler();
    }
  }, [tempFiles]);

  return {
    isUploading,
    setIsUploading,
    fileUploadProgress,
    setFileUploadProgress,
    tempFiles,
    setTempFiles,
    fileError: debouncedError, // Use debounced error to prevent flashing
    pdfPages,
    onDrop,
    onDropRejected,
  };
};
